import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Title, Text, Flex, Paper, Modal, TextInput, Button, Group, Alert } from '@mantine/core';
import { AlertCircle } from 'lucide-react';
import { initializeCollections, addCollection, updateCollection, deleteCollection } from '../redux/actions/collectionActions';
import { useAppDispatch } from '../redux/hooks';
import { CollectionResponse } from '../models/responses/CollectionResponse';
import { buildFolderTree, findNodeById, FolderNode } from '../components/folderExplorer/folderUtils';
import FolderTree from '../components/folderExplorer/FolderTree';
import FolderDetailsPanel from '../components/folderExplorer/FolderDetailsPanel';
import { deleteDocument, getFilteredFiles, uploadFilesToCollection } from '../api/uploadedFileApi';
import { useAuth } from '../customAuth/authStateHook';

const TREE_PANEL_WIDTH = 280;
const ACCEPTED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

type ModalMode = 'newFolder' | 'rename' | 'delete' | 'deleteFile' | 'folderNotEmpty' | null;
type UploadStatus = { type: 'success' | 'error'; message: string } | null;

const getDocumentId = (doc: any): number | null => {
  const id = doc?.id ?? doc?.Id;
  return typeof id === 'number' ? id : null;
};

const getDocumentName = (doc: any): string => doc?.fileName ?? doc?.FileName ?? 'this file';

export default function FolderExplorerPage() {
  const { collections, isLoading } = useSelector((state: any) => state.collection);
  const dispatch = useAppDispatch();
  const { user, isSuperAdmin } = useAuth();

  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [folderDocuments, setFolderDocuments] = useState<any[]>([]);
  const [folderTotalCount, setFolderTotalCount] = useState<number | undefined>(undefined);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);
  const [documentToDelete, setDocumentToDelete] = useState<any | null>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(null);

  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [nameInput, setNameInput] = useState('');
  const [isOperating, setIsOperating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(initializeCollections());
  }, [dispatch]);

  const folderTree = useMemo(
    () => buildFolderTree(collections as CollectionResponse[]),
    [collections]
  );

  useEffect(() => {
    if (selectedFolderId === null && folderTree.length > 0) {
      setSelectedFolderId(folderTree[0].id);
    }
  }, [folderTree, selectedFolderId]);

  const selectedFolder = useMemo(
    () => selectedFolderId !== null ? findNodeById(folderTree, selectedFolderId) ?? null : null,
    [folderTree, selectedFolderId]
  );

  useEffect(() => {
    setDocumentToDelete(null);
  }, [selectedFolderId]);

  useEffect(() => {
    if (selectedFolderId === null) {
      setFolderDocuments([]);
      setFolderTotalCount(undefined);
      return;
    }
    let cancelled = false;
    setIsLoadingDocuments(true);

    getFilteredFiles({ collectionIds: [selectedFolderId], pageSize: 0 }).then(res => {
      if (!cancelled) setFolderTotalCount(res.totalCount ?? undefined);
    }).catch(() => {
      if (!cancelled) setFolderTotalCount(undefined);
    });

    getFilteredFiles({ collectionIds: [selectedFolderId], pageSize: 1000 }).then(result => {
      if (!cancelled) {
        setFolderDocuments(result.items ?? []);
        setIsLoadingDocuments(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setFolderDocuments([]);
        setIsLoadingDocuments(false);
      }
    });
    return () => { cancelled = true; };
  }, [selectedFolderId, documentsRefreshKey]);

  const refreshDocuments = () => setDocumentsRefreshKey(k => k + 1);

  const handleRefresh = () => {
    setErrorMessage(null);
    setUploadStatus(null);
    dispatch(initializeCollections());
    refreshDocuments();
  };

  const handleSelect = (node: FolderNode) => {
    setSelectedFolderId(node.id);
    setUploadStatus(null);
    setErrorMessage(null);
  };

  const openModal = (mode: ModalMode) => {
    setErrorMessage(null);
    setNameInput(mode === 'rename' && selectedFolder ? selectedFolder.name : '');
    setModalMode(mode);
  };

  const handleDeleteFolderClick = () => {
    if (!selectedFolder) return;
    const hasChildren = selectedFolder.children.length > 0;
    const hasDocuments = (typeof folderTotalCount === 'number' ? folderTotalCount : folderDocuments.length) > 0;
    openModal(hasChildren || hasDocuments ? 'folderNotEmpty' : 'delete');
  };

  const openDeleteFileModal = (doc: any) => {
    setErrorMessage(null);
    setDocumentToDelete(doc);
    setModalMode('deleteFile');
  };

  const closeModal = () => {
    setModalMode(null);
    setNameInput('');
    setErrorMessage(null);
    setDocumentToDelete(null);
  };

  // Upload handler — drag-and-drop OR toolbar button
  const handleUploadFiles = async (files: File[]) => {
    if (!selectedFolderId || !selectedFolder) return;

    const valid = files.filter(f => ACCEPTED_MIME.includes(f.type));
    if (valid.length === 0) {
      setUploadStatus({ type: 'error', message: 'No supported files. Use PDF, DOC, or DOCX.' });
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);

    try {
      const result = await uploadFilesToCollection(
        valid,
        [selectedFolderId],
        user?.email ?? '',
        user?.organizationId,
      );

      if (result) {
        const uploaded = result.uploaded_count ?? 0;
        const relinked = result.relinked_count ?? 0;
        const failed = (result.failed_files ?? []).length;
        const dupes = (result.duplicated_files ?? []).length;
        const large = (result.large_files ?? []).length;
        const visible = uploaded + relinked; // files that now appear in this folder

        if (visible > 0) {
          const parts: string[] = [];
          if (uploaded > 0) parts.push(`${uploaded} new file${uploaded !== 1 ? 's' : ''} uploaded`);
          if (relinked > 0) parts.push(`${relinked} existing file${relinked !== 1 ? 's' : ''} added to this folder`);
          if (failed > 0) parts.push(`${failed} failed`);
          if (large > 0) parts.push(`${large} too large`);
          if (dupes > 0) parts.push(`${dupes} already in this folder`);
          setUploadStatus({ type: 'success', message: parts.join(', ') + '.' });
          refreshDocuments();
        } else if (dupes > 0 && failed === 0 && large === 0) {
          setUploadStatus({
            type: 'error',
            message: `${dupes === 1 ? 'This file is' : 'All files are'} already in this folder.`,
          });
        } else {
          setUploadStatus({
            type: 'error',
            message: `Upload completed but no files were added. Failed: ${failed}, Already in this folder: ${dupes}, Too large: ${large}.`,
          });
        }
      } else {
        setUploadStatus({ type: 'error', message: 'Upload failed. Please try again.' });
      }
    } catch (err: any) {
      setUploadStatus({
        type: 'error',
        message: err?.response?.data?.errorMessage ?? err?.message ?? 'Upload failed.',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClickUpload = () => fileInputRef.current?.click();

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) handleUploadFiles(files);
    e.target.value = '';
  };

  const handleConfirm = async () => {
    if (!nameInput.trim() && modalMode !== 'delete' && modalMode !== 'deleteFile') return;

    setIsOperating(true);
    setErrorMessage(null);

    try {
      if (modalMode === 'newFolder' && selectedFolder) {
        const isSystemNode = selectedFolder.originalCollection.IsSystemCollection === true;
        const parentCollectionId = isSystemNode ? null : selectedFolder.id;
        const result: any = await dispatch(addCollection({ name: nameInput.trim(), parentCollectionId }));
        if (!result?.success) {
          setErrorMessage(result?.error || 'Failed to create folder.');
          setIsOperating(false);
          return;
        }
        const newId = result.data?.id ?? result.data?.Id ?? null;
        if (newId && !isSystemNode) {
        } else if (newId && isSystemNode) {
        }
      }

      if (modalMode === 'rename' && selectedFolder) {
        const updated: CollectionResponse = { ...selectedFolder.originalCollection, Name: nameInput.trim() };
        const result: any = await dispatch(updateCollection(updated));
        if (!result?.success) {
          setErrorMessage(result?.error || 'Failed to rename folder.');
          setIsOperating(false);
          return;
        }
      }

      if (modalMode === 'delete' && selectedFolder) {
        const parentId = selectedFolder.parentCollectionId;
        const result: any = await dispatch(deleteCollection(selectedFolder.id));
        if (!result?.success) {
          setErrorMessage(result?.error || 'Failed to delete folder.');
          setIsOperating(false);
          return;
        }
        setSelectedFolderId(parentId);
      }

      if (modalMode === 'deleteFile' && documentToDelete) {
        const docId = getDocumentId(documentToDelete);
        if (docId === null) {
          setErrorMessage('Could not determine which file to delete.');
          setIsOperating(false);
          return;
        }
        const result: any = await deleteDocument(docId);
        if (!result?.success) {
          setErrorMessage(result?.message || 'Failed to delete file. Please try again.');
          setIsOperating(false);
          return;
        }
        setFolderDocuments(prev => prev.filter(d => getDocumentId(d) !== docId));
        setFolderTotalCount(prev => (typeof prev === 'number' ? Math.max(0, prev - 1) : prev));
        setUploadStatus({ type: 'success', message: result?.message || 'File deleted successfully.' });
        refreshDocuments();
      }

      closeModal();
    } finally {
      setIsOperating(false);
    }
  };

  const isSystemSelected = selectedFolder?.originalCollection.IsSystemCollection === true;

  const modalTitle = modalMode === 'newFolder'
    ? (isSystemSelected ? 'New Root Folder' : `New Folder in "${selectedFolder?.name}"`)
    : modalMode === 'rename' ? `Rename "${selectedFolder?.name}"`
    : modalMode === 'delete' ? `Delete "${selectedFolder?.name}"`
    : modalMode === 'deleteFile' ? `Delete "${getDocumentName(documentToDelete)}"`
    : modalMode === 'folderNotEmpty' ? `Cannot delete "${selectedFolder?.name}"`
    : '';

  const isDeleteMode = modalMode === 'delete';
  const isDeleteFileMode = modalMode === 'deleteFile';
  const isFolderNotEmptyMode = modalMode === 'folderNotEmpty';
  const confirmDisabled = isOperating || (!isDeleteMode && !isDeleteFileMode && !nameInput.trim());

  const folderNotEmptyMessage = (() => {
    if (!selectedFolder) return '';
    const hasChildren = selectedFolder.children.length > 0;
    const hasDocuments = (typeof folderTotalCount === 'number' ? folderTotalCount : folderDocuments.length) > 0;
    if (hasChildren && hasDocuments) {
      return 'This folder cannot be deleted because it contains documents and subfolders. Remove or move them first.';
    }
    if (hasChildren) {
      return 'This folder cannot be deleted because it contains subfolders. Remove or move them first.';
    }
    return 'This folder cannot be deleted because it contains documents. Delete or move them first.';
  })();

  return (
    <Box p="xl" style={{ minHeight: '100vh' }}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      <Box mb="lg">
        <Title fw={700} order={2} className="page-title">Folder Explorer</Title>
        <Text size="sm" c="dimmed">Manage library folders and subfolders.</Text>
      </Box>

      <Flex
        gap="md"
        style={{ height: 'calc(100vh - 160px)', minHeight: 400 }}
        align="stretch"
      >
        <Paper
          shadow="xs"
          radius="md"
          p="sm"
          style={{
            width: TREE_PANEL_WIDTH,
            minWidth: TREE_PANEL_WIDTH,
            background: '#faf8f3',
            border: '1px solid #e8e0d4',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Text size="xs" fw={600} c="dimmed" mb="xs" px={8} style={{ letterSpacing: '0.04em' }}>
            FOLDERS
          </Text>
          <FolderTree
            nodes={folderTree}
            selectedId={selectedFolderId}
            onSelect={handleSelect}
            isLoading={isLoading}
          />
        </Paper>

        {/* Right content panel */}
        <Paper
          shadow="xs"
          radius="md"
          style={{
            flex: 1,
            background: '#ffffff',
            border: '1px solid #e8e0d4',
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          <FolderDetailsPanel
            selectedFolder={selectedFolder}
            allCollections={collections as CollectionResponse[]}
            onSelect={handleSelect}
            documents={folderDocuments}
            totalCount={folderTotalCount}
            isLoadingDocuments={isLoadingDocuments}
            onUpload={handleUploadFiles}
            isUploading={isUploading}
            uploadStatus={uploadStatus}
            onNewFolder={() => openModal('newFolder')}
            onRename={() => openModal('rename')}
            onDelete={handleDeleteFolderClick}
            onRefresh={handleRefresh}
            onClickUpload={handleClickUpload}
            isOperating={isOperating}
            onDeleteFile={openDeleteFileModal}
            isSuperAdmin={isSuperAdmin}
          />
        </Paper>
      </Flex>

      {/* Modal */}
      <Modal
        opened={modalMode !== null}
        onClose={closeModal}
        title={modalTitle}
        centered
        size="sm"
      >
        {errorMessage && (
          <Alert icon={<AlertCircle size={16} />} color="red" mb="md">
            {errorMessage}
          </Alert>
        )}

        {/* Guarded on modalMode !== null: Mantine keeps the Modal's children
            mounted during its closing fade-out (opened=false plays a transition
            rather than unmounting instantly). closeModal() sets modalMode to
            null in that same tick, so without this guard every one of these
            branches would fall through to the New Folder form below for the
            duration of that transition - a visible flash of the wrong modal
            content on every close, regardless of which modal was open. */}
        {modalMode !== null && (isDeleteMode ? (
          <Text size="sm" c="dimmed" mt="sm" mb="lg">
            Are you sure you want to delete <strong>{selectedFolder?.name}</strong>?
            This cannot be undone.
          </Text>
        ) : isDeleteFileMode ? (
          <Text size="sm" c="dimmed" mt="sm" mb="lg">
            Are you sure you want to delete <strong>{getDocumentName(documentToDelete)}</strong>?
            This action is permanent and cannot be undone.
          </Text>
        ) : isFolderNotEmptyMode ? (
          <Text size="sm" c="dimmed" mt="sm" mb="lg">
            {folderNotEmptyMessage}
          </Text>
        ) : (
          <TextInput
            label="Folder name"
            placeholder="Enter folder name"
            value={nameInput}
            onChange={e => setNameInput(e.currentTarget.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleConfirm(); }}
            data-autofocus
            mb="md"
          />
        ))}

        {modalMode !== null && (isFolderNotEmptyMode ? (
          <Group justify="flex-end" gap="xs">
            <Button size="xs" color="dark" onClick={closeModal}>
              OK
            </Button>
          </Group>
        ) : (
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" color="dark" size="xs" onClick={closeModal} disabled={isOperating}>
              Cancel
            </Button>
            <Button
              size="xs"
              color={isDeleteMode || isDeleteFileMode ? 'red' : 'dark'}
              onClick={handleConfirm}
              loading={isOperating}
              disabled={confirmDisabled}
            >
              {isDeleteMode ? 'Delete folder' : isDeleteFileMode ? 'Delete file' : 'Confirm'}
            </Button>
          </Group>
        ))}
      </Modal>
    </Box>
  );
}
